import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/** "an" before vowel sounds (AWS, ISC2); "a" otherwise. */
export function indefiniteArticle(phrase: string): "a" | "an" {
  return /^[aeiou]/i.test(phrase.trim()) ? "an" : "a";
}

/**
 * Formats a US phone number as the user types (e.g., 555-123-4567).
 * Only digits are allowed; extra digits are ignored.
 */
export function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

/**
 * Formats a number as currency (USD) with decimal support
 */
export function formatCurrency(amount: number | string): string {
  const numericAmount = typeof amount === 'string' ? parseFloat(amount) || 0 : amount || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numericAmount);
}

/**
 * Parses currency string to number, supporting decimals
 */
export function parseCurrency(value: string): number {
  // Remove currency symbols and keep only digits and decimal point
  const cleaned = value.replace(/[^\d.]/g, "");
  // Ensure only one decimal point
  const parts = cleaned.split('.');
  if (parts.length > 2) {
    const final = parts[0] + '.' + parts.slice(1).join('');
    return parseFloat(final) || 0;
  }
  return parseFloat(cleaned) || 0;
}

/**
 * Formats currency value for form inputs (without $ symbol)
 */
export function formatCurrencyInput(value: number | string | undefined): string {
  if (!value) return "";
  const numericAmount = typeof value === 'string' ? parseFloat(value) || 0 : value || 0;
  return numericAmount.toString();
}
